import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ROUTES } from '../config/constants'

const uploadSteps = [
  { num: '01', title: 'Paste Drive Link', desc: 'Upload your full match recording via Google Drive.' },
  { num: '02', title: 'We Edit Like Pros', desc: 'AI + human editors pick the best moments with cinematic cuts.' },
  { num: '03', title: 'Get Social-Ready Videos', desc: 'Receive Reels, Shorts, and YouTube highlights ready to post.' },
]

const toolSteps = [
  { num: '01', title: 'Download the Free Tool', desc: 'Install on Windows or Mac. No dependencies needed.' },
  { num: '02', title: 'Load Your Match Video', desc: 'Drag and drop your raw match file directly.' },
  { num: '03', title: 'Mark & Assign Clips', desc: 'Set timestamps and assign them to Player Bags.' },
  { num: '04', title: 'Export All Reels', desc: 'One click generates separate highlight reels per player.' },
]

function PathCard({ steps, title, subtitle, ctaText, ctaLink, accentColor, icon, isPrimary }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className={`rounded-3xl p-8 md:p-10 border backdrop-blur-sm relative overflow-hidden flex flex-col ${
        isPrimary
          ? 'bg-gradient-to-br from-pitch-green/[0.08] to-transparent border-pitch-green/20'
          : 'bg-white/[0.02] border-white/[0.08]'
      }`}
    >
      {/* Background accent */}
      <div
        className="absolute top-0 right-0 w-40 h-40 rounded-full blur-[100px] opacity-20 pointer-events-none"
        style={{ backgroundColor: accentColor }}
      />

      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
          style={{ backgroundColor: accentColor + '20' }}
        >
          {icon}
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>

      {/* Steps */}
      <div className="flex-1 mt-8 space-y-5">
        {steps.map((step, i) => (
          <motion.div
            key={step.num}
            initial={{ opacity: 0, x: -15 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12 }}
            className="flex items-start gap-4 group"
          >
            <div className="flex flex-col items-center">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border flex-shrink-0 transition-all group-hover:scale-110"
                style={{
                  borderColor: accentColor + '40',
                  color: accentColor,
                  backgroundColor: accentColor + '10',
                }}
              >
                {step.num}
              </div>
              {i < steps.length - 1 && (
                <div className="w-px h-6 mt-1" style={{ backgroundColor: accentColor + '20' }} />
              )}
            </div>
            <div className="pt-1">
              <p className="text-white font-semibold text-sm">{step.title}</p>
              <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{step.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <Link
        to={ctaLink}
        className={`mt-8 inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-sm transition-all hover:scale-105 ${
          isPrimary
            ? 'bg-pitch-green text-stadium-black shadow-lg shadow-pitch-green/20 hover:bg-neon-green'
            : 'border-2 border-white/20 text-white hover:border-pitch-green/50 hover:text-pitch-green'
        }`}
      >
        {ctaText}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </Link>
    </motion.div>
  )
}

export default function TwoPaths() {
  return (
    <section className="py-20 md:py-28 px-4 relative">
      {/* Section divider */}
      <div className="section-divider max-w-4xl mx-auto mb-20" />

      <div className="container mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="inline-block text-xs font-bold text-pitch-green uppercase tracking-[0.2em] mb-4">
            How It Works
          </span>
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            <span className="text-white">Choose Your </span>
            <span className="gradient-text">Workflow</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Let us handle the editing, or take full control with the desktop tool.
          </p>
        </motion.div>

        {/* Two Paths Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <PathCard
            steps={uploadSteps}
            title="Upload Service"
            subtitle="We edit for you"
            ctaText="Upload Your Match"
            ctaLink={ROUTES.UPLOAD}
            accentColor="#ffd700"
            icon="📎"
            isPrimary={false}
          />
          <PathCard
            steps={toolSteps}
            title="Desktop Tool"
            subtitle="Do it yourself — Free"
            ctaText="Download Free Tool"
            ctaLink={ROUTES.DOWNLOAD}
            accentColor="#00ff88"
            icon="🖥️"
            isPrimary={true}
          />
        </div>
      </div>
    </section>
  )
}
